"use client"

import { ThemeMode } from "@prisma/client"
import {
  useCallback,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react"

import type { ThemeRecord, ThemeUpdateInput } from "@/lib/theme/theme.types"

type ThemeProviderProps = {
  children: ReactNode
  initialLightTheme: ThemeRecord
  initialDarkTheme: ThemeRecord
}

type ThemeContextValue = {
  lightTheme: ThemeRecord
  darkTheme: ThemeRecord
  activeTheme: ThemeRecord
  updateTheme: (mode: ThemeMode, updates: ThemeUpdateInput) => void
  resetTheme: (mode?: ThemeMode) => void
}

const ThemeContext = createContext<ThemeContextValue | null>(null)

const HEX_COLOR_PATTERN = /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/

function sanitizeColor(value: string, fallback: string) {
  return HEX_COLOR_PATTERN.test(value) ? value : fallback
}

function getActiveMode() {
  if (typeof document === "undefined") return ThemeMode.LIGHT

  const root = document.documentElement
  if (root.classList.contains("theme-dark")) return ThemeMode.DARK
  if (root.classList.contains("theme-light")) return ThemeMode.LIGHT

  const saved = window.localStorage.getItem("theme")
  if (saved === "dark") return ThemeMode.DARK
  if (saved === "light") return ThemeMode.LIGHT

  return window.matchMedia("(prefers-color-scheme: dark)").matches
    ? ThemeMode.DARK
    : ThemeMode.LIGHT
}

function subscribeToThemeMode(onStoreChange: () => void) {
  if (typeof document === "undefined") {
    return () => {}
  }

  const observer = new MutationObserver(() => {
    onStoreChange()
  })

  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["class"],
  })

  return () => observer.disconnect()
}

function applyThemeVariables(theme: ThemeRecord) {
  if (typeof document === "undefined") return

  const rootStyle = document.documentElement.style

  rootStyle.setProperty("--background", sanitizeColor(theme.background, theme.background))
  rootStyle.setProperty(
    "--background-secondary",
    sanitizeColor(theme.backgroundSecondary, theme.backgroundSecondary)
  )
  rootStyle.setProperty("--surface", sanitizeColor(theme.surface, theme.surface))
  rootStyle.setProperty(
    "--surface-secondary",
    sanitizeColor(theme.backgroundSecondary, theme.backgroundSecondary)
  )
  rootStyle.setProperty("--card", sanitizeColor(theme.surface, theme.surface))
  rootStyle.setProperty(
    "--card-foreground",
    sanitizeColor(theme.textPrimary, theme.textPrimary)
  )
  rootStyle.setProperty("--popover", sanitizeColor(theme.surface, theme.surface))
  rootStyle.setProperty(
    "--popover-foreground",
    sanitizeColor(theme.textPrimary, theme.textPrimary)
  )
  rootStyle.setProperty("--foreground", sanitizeColor(theme.textPrimary, theme.textPrimary))
  rootStyle.setProperty("--text-primary", sanitizeColor(theme.textPrimary, theme.textPrimary))
  rootStyle.setProperty("--text-strong", sanitizeColor(theme.textPrimary, theme.textPrimary))
  rootStyle.setProperty(
    "--text-secondary",
    sanitizeColor(theme.textSecondary, theme.textSecondary)
  )
  rootStyle.setProperty(
    "--text-tertiary",
    sanitizeColor(theme.textSecondary, theme.textSecondary)
  )
  rootStyle.setProperty("--text-muted", sanitizeColor(theme.textSecondary, theme.textSecondary))
  rootStyle.setProperty("--primary", sanitizeColor(theme.primary, theme.primary))
  rootStyle.setProperty(
    "--primary-hover",
    sanitizeColor(theme.primaryHover, theme.primaryHover)
  )
  rootStyle.setProperty("--link", sanitizeColor(theme.link, theme.link))
  rootStyle.setProperty("--link-hover", sanitizeColor(theme.linkHover, theme.linkHover))
  rootStyle.setProperty(
    "--menu-item-hover",
    sanitizeColor(theme.menuItemHover, theme.menuItemHover)
  )
  rootStyle.setProperty(
    "--submenu-item-hover",
    sanitizeColor(theme.submenuItemHover, theme.submenuItemHover)
  )
  rootStyle.setProperty("--primary-foreground", "#ffffff")
  rootStyle.setProperty(
    "--secondary",
    sanitizeColor(theme.backgroundSecondary, theme.backgroundSecondary)
  )
  rootStyle.setProperty(
    "--secondary-foreground",
    sanitizeColor(theme.textPrimary, theme.textPrimary)
  )
  rootStyle.setProperty(
    "--muted",
    sanitizeColor(theme.backgroundSecondary, theme.backgroundSecondary)
  )
  rootStyle.setProperty(
    "--muted-foreground",
    sanitizeColor(theme.textSecondary, theme.textSecondary)
  )
  rootStyle.setProperty(
    "--accent",
    sanitizeColor(theme.backgroundSecondary, theme.backgroundSecondary)
  )
  rootStyle.setProperty(
    "--accent-foreground",
    sanitizeColor(theme.textPrimary, theme.textPrimary)
  )
  rootStyle.setProperty("--success", sanitizeColor(theme.success, theme.success))
  rootStyle.setProperty("--danger", sanitizeColor(theme.danger, theme.danger))
  rootStyle.setProperty("--destructive", sanitizeColor(theme.danger, theme.danger))
  rootStyle.setProperty("--destructive-foreground", "#ffffff")
  rootStyle.setProperty("--warning", sanitizeColor(theme.warning, theme.warning))
  rootStyle.setProperty("--info", sanitizeColor(theme.info, theme.info))
  rootStyle.setProperty("--border", sanitizeColor(theme.border, theme.border))
  rootStyle.setProperty("--border-default", sanitizeColor(theme.border, theme.border))
  rootStyle.setProperty("--border-strong", sanitizeColor(theme.border, theme.border))
  rootStyle.setProperty("--input", sanitizeColor(theme.inputBorder, theme.inputBorder))
  rootStyle.setProperty("--input-background", sanitizeColor(theme.inputBackground, theme.inputBackground))
  rootStyle.setProperty("--input-text", sanitizeColor(theme.inputText, theme.inputText))
  rootStyle.setProperty("--input-border", sanitizeColor(theme.inputBorder, theme.inputBorder))
  rootStyle.setProperty(
    "--input-focus-border",
    sanitizeColor(theme.inputFocusBorder, theme.inputFocusBorder)
  )
  rootStyle.setProperty(
    "--dropdown-background",
    sanitizeColor(theme.dropdownBackground, theme.dropdownBackground)
  )
  rootStyle.setProperty("--dropdown-text", sanitizeColor(theme.dropdownText, theme.dropdownText))
  rootStyle.setProperty("--dropdown-hover", sanitizeColor(theme.dropdownHover, theme.dropdownHover))
  rootStyle.setProperty("--range-track", sanitizeColor(theme.rangeTrack, theme.rangeTrack))
  rootStyle.setProperty("--range-thumb", sanitizeColor(theme.rangeThumb, theme.rangeThumb))
  rootStyle.setProperty(
    "--header-background",
    sanitizeColor(theme.headerBackground, theme.headerBackground)
  )
  rootStyle.setProperty("--header-text", sanitizeColor(theme.headerText, theme.headerText))
  rootStyle.setProperty("--header-border", sanitizeColor(theme.headerBorder, theme.headerBorder))
  rootStyle.setProperty(
    "--footer-background",
    sanitizeColor(theme.footerBackground, theme.footerBackground)
  )
  rootStyle.setProperty("--footer-text", sanitizeColor(theme.footerText, theme.footerText))
  rootStyle.setProperty("--footer-border", sanitizeColor(theme.footerBorder, theme.footerBorder))
  rootStyle.setProperty("--switch-background", sanitizeColor(theme.rangeTrack, theme.rangeTrack))
  rootStyle.setProperty("--ring", sanitizeColor(theme.primary, theme.primary))
}

export default function ThemeProvider({
  children,
  initialLightTheme,
  initialDarkTheme,
}: ThemeProviderProps) {
  const [lightTheme, setLightTheme] = useState(initialLightTheme)
  const [darkTheme, setDarkTheme] = useState(initialDarkTheme)
  const activeMode = useSyncExternalStore(subscribeToThemeMode, getActiveMode, () => ThemeMode.LIGHT)

  const activeTheme = activeMode === ThemeMode.DARK ? darkTheme : lightTheme

  useEffect(() => {
    applyThemeVariables(activeTheme)
  }, [activeTheme])

  const updateTheme = useCallback((mode: ThemeMode, updates: ThemeUpdateInput) => {
    if (mode === ThemeMode.DARK) {
      setDarkTheme((current) => ({ ...current, ...updates }))
      return
    }

    setLightTheme((current) => ({ ...current, ...updates }))
  }, [])

  const resetTheme = useCallback((mode?: ThemeMode) => {
    if (!mode || mode === ThemeMode.LIGHT) {
      setLightTheme(initialLightTheme)
    }

    if (!mode || mode === ThemeMode.DARK) {
      setDarkTheme(initialDarkTheme)
    }
  }, [initialDarkTheme, initialLightTheme])

  const value = useMemo<ThemeContextValue>(
    () => ({
      lightTheme,
      darkTheme,
      activeTheme,
      updateTheme,
      resetTheme,
    }),
    [activeTheme, darkTheme, lightTheme, resetTheme, updateTheme]
  )

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
}

export function useTheme() {
  const context = useContext(ThemeContext)

  if (!context) {
    throw new Error("useTheme must be used within ThemeProvider")
  }

  return context
}
